import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  EmptyState,
  ErrorState,
  NoPermissionState,
  PageHeader,
  Skeleton,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCan } from '@/features/auth/session';
import { duplicatesQueryOptions } from '@/features/crm/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatDateShort } from '@/lib/format';

export const Route = createFileRoute('/_app/crm/duplicates')({ component: DuplicatesPage });

/** Doublons probables (ADR-025, R-CRM-06) : la fusion se fait depuis la fiche à conserver. */
function DuplicatesPage() {
  const { t, i18n } = useTranslation(['crm', 'customers', 'common', 'errors']);
  const canMerge = useCan('customer.merge');
  const query = useQuery(duplicatesQueryOptions(canMerge));

  if (!canMerge) {
    return (
      <Container>
        <PageHeader title={t('crm:duplicates.title')} />
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionBody')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('crm:duplicates.title')}
        subtitle={t('crm:duplicates.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('crm:title'), link: <Link to="/crm">{t('crm:title')}</Link> },
          { label: t('crm:duplicates.title') },
        ]}
      />
      {query.isError ? (
        <ErrorState
          title={t('errors:generic.title')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:trace.copy')}
          traceCopiedLabel={t('common:trace.copied')}
        />
      ) : query.isPending ? (
        <Skeleton height={200} />
      ) : query.data.length === 0 ? (
        <EmptyState
          icon={<Copy className="ico ico-24" aria-hidden="true" />}
          title={t('crm:duplicates.empty')}
        />
      ) : (
        <div className="stack">
          {query.data.map((group) => (
            <Card key={`${group.criterion}-${group.value}`}>
              <CardHeader
                title={`${t(`crm:duplicates.criterion.${group.criterion}`)} : ${group.value}`}
                icon={<Copy className="ico ico-16" aria-hidden="true" />}
                iconTone="amber"
              />
              <CardBody>
                <ul className="list-plain stack stack--sm">
                  {group.customers.map((customer) => (
                    <li key={customer.id} className="row row--wrap" style={{ gap: 8 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <span className="strong">{customer.displayName}</span>{' '}
                        <Badge tone={customer.kind === 'PERSON' ? 'violet' : 'blue'}>
                          {t(`customers:kind.${customer.kind}`)}
                        </Badge>
                        <div className="muted t-12">
                          {t('crm:duplicates.visits', { count: customer.visits })} ·{' '}
                          {t('crm:duplicates.createdAt', {
                            date: formatDateShort(customer.createdAt, i18n.language),
                          })}
                          {customer.email ? ` · ${customer.email}` : ''}
                        </div>
                      </div>
                      <Button variant="secondary" size="sm" asChild>
                        <Link to="/customers/$customerId" params={{ customerId: customer.id }}>
                          {t('crm:detail.openRecord')}
                        </Link>
                      </Button>
                    </li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </Container>
  );
}
